import { useTranslation } from "react-i18next";
import { Icon } from "@/components/Icon";
import { NotificationsModal } from "@/components/NotificationsModal";
import { Modal } from "@/components/Modal";
import { Button } from "@/components/Button";
import { AppShell } from "@/components/workspace/AppShell";
import { useWorkspaceLayout } from "@/components/workspace/useWorkspaceLayout";
import type { ReactNode } from "react";
import { useAuth } from "@/context/AuthContext";

interface AppLayoutProps {
    children: ReactNode;
}
export const AppLayout = ({ children }: AppLayoutProps) => {
    const { t } = useTranslation("global");
    const {
        permissionEditMode,
        setPermissionEditMode,
        impersonatedBy,
        stopImpersonation,
    } = useAuth();
    const {
        user,
        isIt,
        isAdmin,
        isDevelopment,
        logout,
        companies,
        companiesLoaded,
        activeCompanyId,
        activeCompany,
        sortedCompanies,
        handleCompanyChange,
        getInitials,
        orderedNavItems,
        favoriteMenuIds,
        toggleFavorite,
        updateFavorites,
        isNotificationsModalOpen,
        setIsNotificationsModalOpen,
        unreadNotificationsCount,
        setUnreadNotificationsCount,
        isCompanyModalOpen,
        setIsCompanyModalOpen,
        userProcesses,
        processNotificationIds,
        setProcessNotification,
    } = useWorkspaceLayout();
    if (
        companiesLoaded &&
        companies.length === 0 &&
        !isAdmin &&
        !isIt &&
        !isDevelopment
    ) {
        return (
            <div className="min-h-screen bg-page flex items-center justify-center p-4">
                <div className="bg-white dark:bg-zinc-900 rounded-2xl shadow-xl p-8 max-w-md w-full text-center space-y-6 animate-fade-in border border-slate-200 dark:border-dark-border">
                    <div className="w-16 h-16 bg-rose-100 text-rose-500 rounded-full flex items-center justify-center mx-auto shadow-sm">
                        <Icon value="building" size={32} />
                    </div>
                    <div className="space-y-2">
                        <h1 className="text-xl font-bold text-slate-800 dark:text-white">
                            {t("workspace.no_access")}
                        </h1>
                        <p className="text-slate-500 dark:text-slate-400 text-sm">
                            {t("workspace.no_access_description")}
                        </p>
                        <p className="text-slate-500 dark:text-slate-400 text-sm font-medium">
                            {t("workspace.no_access_help")}
                        </p>
                    </div>
                    <Button
                        variant="secondary"
                        fullWidth
                        onClick={() => logout()}
                    >
                        {t("workspace.logout")}
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className={`min-h-screen ${permissionEditMode || impersonatedBy ? "pt-10" : ""}`}>
            {impersonatedBy && (
                <div className="impersonation-banner fixed inset-x-0 top-0 z-[1000000] flex min-h-10 items-center justify-center gap-3 px-4 py-2 text-sm text-amber-900 shadow-sm dark:text-amber-100">
                    <div className="flex items-center gap-1.5">
                        <div>{t("users.impersonating")}</div>
                        <div className="font-bold">
                            #{user?.id} {user?.name} - {user?.email}
                        </div>
                    </div>
                    <Button variant="black" size="sm" onClick={stopImpersonation}>
                        {t("common.back")}
                    </Button>
                </div>
            )}
            {permissionEditMode && (
                <div className="pointer-events-none fixed inset-0 z-[1000001] box-border animate-pulse border-4 border-rose-500" />
            )}
            {impersonatedBy && (
                <div className="pointer-events-none fixed inset-0 z-[1000000] box-border animate-pulse border-4 border-amber-400" />
            )}
            {permissionEditMode && (
                <div className="fixed inset-x-0 top-0 z-[999999] flex min-h-10 w-full animate-pulse items-center justify-center gap-3 bg-rose-100 px-4 py-2 text-sm font-semibold text-rose-700 shadow-sm dark:bg-rose-950/60 dark:text-rose-200">
                    <div>{t("tools.permissions.mode_edit")}</div>
                    <Button                        
                        variant="error"
                        size="sm"
                        onClick={() => setPermissionEditMode(false)}
                    >
                        {t("tools.permissions.exit_mode")}
                    </Button>
                </div>
            )}
            <AppShell
                items={orderedNavItems}
                favorites={favoriteMenuIds}
                onFavorite={toggleFavorite}
                onFavoritesChange={updateFavorites}
                companyName={
                    activeCompany?.name ||
                    localStorage.getItem("vigepra_active_company_name") ||
                    t("workspace.no_company")
                }
                companyLogo={activeCompany?.logo_url}
                brandColor={activeCompany?.brand_color}
                canSwitchCompany={companies.length > 1}
                onCompany={() => setIsCompanyModalOpen(true)}
                user={user}
                scope={`${user?.id}:${activeCompanyId}`}
                unreadCount={unreadNotificationsCount}
                onNotifications={() => setIsNotificationsModalOpen(true)}
                processCount={userProcesses.filter((process) => ["pending", "running"].includes(process.status)).length}
                processes={userProcesses}
                processNotificationIds={processNotificationIds}
                onProcessNotificationChange={setProcessNotification}
            >
                {children}
            </AppShell>

            {/* Notifications Modal */}
            <NotificationsModal
                isOpen={isNotificationsModalOpen}
                onClose={() => setIsNotificationsModalOpen(false)}
                unreadCount={unreadNotificationsCount}
                onCountUpdated={setUnreadNotificationsCount}
            />

            {/* Company Switcher Modal */}
            <Modal
                isOpen={isCompanyModalOpen}
                onClose={() => setIsCompanyModalOpen(false)}
                title={t("companies.switch_title")}
                maxWidth="md"
                footer={
                    <button
                        type="button"
                        onClick={() => setIsCompanyModalOpen(false)}
                        className="btn secondary flex-1 max-w-[200px]"
                    >
                        {t("common.close")}
                    </button>
                }
            >
                <div className="mt-2 min-h-0 flex-1 space-y-2 overflow-y-auto p-1">
                    {sortedCompanies.map((company) => {
                        const isSelected =
                            String(activeCompanyId) === String(company.id) ||
                            (company.database &&
                                String(activeCompanyId) ===
                                    String(company.database));
                        return (
                            <button
                                key={company.id}
                                onClick={() => {
                                    handleCompanyChange(company.id);
                                    setIsCompanyModalOpen(false);
                                }}
                                className={`w-full flex items-center gap-4 p-3 rounded-xl transition-all cursor-pointer ${
                                    isSelected
                                        ? "bg-primary/10 border border-primary/20 shadow-sm"
                                        : "bg-white dark:bg-zinc-900 border border-slate-200 dark:border-white/10 hover:border-primary/50 hover:shadow-md"
                                }`}
                            >
                                <div
                                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-white shadow-sm"
                                >
                                    {company.logo_url ? (
                                        <img
                                            src={company.logo_url}
                                            alt=""
                                            className="h-full w-full rounded-full object-cover"
                                        />
                                    ) : (
                                        getInitials(company.name)
                                    )}
                                </div>
                                <div className="flex-1 text-left min-w-0">
                                    <div className="font-bold text-slate-900 dark:text-white truncate text-base">
                                        {company.name}
                                    </div>
                                </div>
                                {isSelected && (
                                    <Icon
                                        value="check"
                                        size={20}
                                        className="text-primary shrink-0 font-bold"
                                    />
                                )}
                            </button>
                        );
                    })}
                </div>
            </Modal>
        </div>
    );
};
